import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Edit, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    speed: "",
    price: "",
    description: "",
    features: [""],
    is_popular: false,
    plan_status: "active",
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      toast.error("Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        features: formData.features.filter((f) => f.trim() !== ""),
      };

      if (editingPlan) {
        await axios.put(`${API}/plans/${editingPlan.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Plan updated successfully");
      } else {
        await axios.post(`${API}/plans`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Plan created successfully");
      }
      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error("Failed to save plan");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Plan deleted successfully");
      fetchPlans();
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      speed: plan.speed,
      price: plan.price.toString(),
      description: plan.description || "",
      features: plan.features || [""],
      is_popular: plan.is_popular || false,
      plan_status: plan.plan_status || "active",
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: "",
      speed: "",
      price: "",
      description: "",
      features: [""],
      is_popular: false,
      plan_status: "active",
    });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ""] });
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Plan Management
            </h1>
            <p className="text-slate-600">
              Manage your internet service plans and pricing
            </p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-lg shadow-lg shadow-blue-600/30"
                data-testid="add-plan-btn"
              >
                <Plus className="w-4 h-4" />
                Add Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="p-0 overflow-hidden sm:max-w-[600px]">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle className="text-xl font-bold">
                  {editingPlan ? "Edit Plan" : "Add New Plan"}
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-testid="plan-form"
                >
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 font-semibold">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          Saving client...
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.is_popular}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_popular: e.target.checked,
                          })
                        }
                      />
                      <Label className="text-sm font-medium text-slate-700">
                        Mark as Popular Plan
                      </Label>
                    </div>

                    <div>
                      <Label>Account Status</Label>
                      <Select
                        value={formData.plan_status}
                        onValueChange={(val) =>
                          setFormData({ ...formData, plan_status: val })
                        }
                      >
                        <SelectTrigger data-testid="client-plan_status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Plan Name
                      </Label>
                      <Input
                        data-testid="plan-name-input"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        placeholder="e.g., Basic 30Mbps"
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Speed
                      </Label>
                      <Input
                        data-testid="plan-speed-input"
                        value={formData.speed}
                        onChange={(e) =>
                          setFormData({ ...formData, speed: e.target.value })
                        }
                        placeholder="e.g., 30Mbps"
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Price (₱)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="plan-price-input"
                        value={formData.price}
                        onChange={(e) =>
                          setFormData({ ...formData, price: e.target.value })
                        }
                        placeholder="799"
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Description
                      </Label>
                      <Input
                        data-testid="plan-description-input"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        placeholder="Perfect for browsing and light streaming"
                        className="mt-1.5 rounded-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        Features
                      </Label>
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <Input
                            value={feature}
                            onChange={(e) =>
                              updateFeature(index, e.target.value)
                            }
                            placeholder="e.g., Unlimited Data"
                            className="rounded-lg"
                          />
                          {formData.features.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFeature(index)}
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addFeature}
                        className="mt-2 rounded-lg"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Feature
                      </Button>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700  text-[20px] font-semibold h-14 mt-4"
                      data-testid="plan-submit-btn"
                    >
                      {editingPlan ? "Update Plan" : "Create Plan"}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
              No plans yet. Add your first plan!
            </div>
          ) : (
            plans.map((plan) => (
              <div
                key={plan.id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
                data-testid={`plan-card-${plan.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    {plan.is_popular && (
                      <span className="text-xs font-semibold text-white bg-blue-500 px-2 py-1 rounded">
                        Most Popular
                      </span>
                    )}
                    {plan.plan_status === "active" && (
                      <span className="text-xs font-semibold text-white bg-green-700 px-2 py-1 rounded">
                        Active
                      </span>
                    )}
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                      {plan.speed}
                    </span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {plan.description}
                </p>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-slate-900">
                    ₱{plan.price}
                  </span>
                  <span className="text-slate-600 text-sm">/month</span>
                </div>

                {plan.features && plan.features.length > 0 && (
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-slate-600"
                      >
                        <span className="text-blue-600 mt-0.5">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(plan)}
                    className="flex-1 hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                    data-testid={`edit-plan-${plan.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(plan.id)}
                    className="flex-1 hover:bg-red-50 hover:text-red-600 rounded-lg"
                    data-testid={`delete-plan-${plan.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
